import type { BookingMeta } from '../api/types';

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'] as const;
export const UTM_STORAGE_KEY = 'cbf_utm';

type Store = Pick<Storage, 'getItem' | 'setItem'>;

function safeStore(): Store | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Reads UTM/click ids from a query string. */
export function utmFromSearch(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) out[key] = value.slice(0, 255);
  }
  return out;
}

/**
 * First-touch capture: the landing page stores its UTMs (the plugin prints a tiny snippet
 * site-wide that does the same); later pages never overwrite them.
 */
export function captureUtm(search: string = typeof location !== 'undefined' ? location.search : '', store: Store | null = safeStore()): Record<string, string> {
  let stored: Record<string, string> = {};
  try {
    const raw = store?.getItem(UTM_STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) as Record<string, string>;
  } catch {
    stored = {};
  }
  if (Object.keys(stored).length) return stored;
  const fresh = utmFromSearch(search);
  if (Object.keys(fresh).length) {
    try {
      store?.setItem(UTM_STORAGE_KEY, JSON.stringify(fresh));
    } catch {
      /* storage unavailable */
    }
  }
  return fresh;
}

/** `meta` entries for the booking: UTMs plus a marker identifying this form. */
export function bookingMeta(utm: Record<string, string>, source = 'wp-plugin'): BookingMeta[] {
  const meta: BookingMeta[] = Object.entries(utm).map(([code, value]) => ({ code, value }));
  meta.push({ code: 'form', value: source });
  return meta;
}
